import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, CalendarRange } from "lucide-react";
import { formatDate } from "@/lib/dateFormat";
import { confirmDiscardUnsaved } from "@/lib/unsavedGuard";
import { toast } from "sonner";
import usePermissions from "@/lib/usePermissions";
import ExpensesLedger from "@/components/ExpensesLedger";
import RevenueLedger from "@/components/RevenueLedger";
import PettyCashLedger from "@/components/PettyCashLedger";
import ProjectPnLTab from "@/components/ProjectPnLTab";
import FinanceAnalyticsPanel from "@/components/FinanceAnalyticsPanel";
import LiquidityDashboard from "@/components/LiquidityDashboard";
import ManagementDashboard from "@/components/ManagementDashboard";
import { useAuth } from "@/context/AuthContext";

/* ---------------------------------------------------------------------------
   Finance page — Admin & Founder only (server-side gated via require_feature).
   Feb 2026 — Master Dynamic Analytics Engine: ONE singular full-width
   BarChart with 5 master toggles (Revenue / Expense / Personnel / Category
   Burn / Project Profitability), a Time Navigator, a Project filter that
   cascades into Category/Service Type options, and click-to-filter straight
   into the ledgers below via `presetFilters`.

   Feb 2026 split: the analytics engine itself now lives in
   `FinanceAnalyticsPanel` (components/) — this page just owns data loading
   and the 4 ledger Tabs below it, wiring the two together via
   `onFilterChange` (→ globalPreset for the ledgers) and `onNavigate`
   (→ switch tab + scroll there).
--------------------------------------------------------------------------- */
// Backend returns at most this many rows per ledger when no date window is given (_finance_list_limit default).
const ALL_TIME_ROW_LIMIT = 2000;

export default function Finance() {
  const can = usePermissions();
  const { user } = useAuth();
  const isManager = user?.role === "founder" || user?.role === "admin";
  const [params] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [liquidity, setLiquidity] = useState(null); // Cash & Liquidity Hub — Total Studio Cash widget
  const [activeTab, setActiveTab] = useState("expenses-ledger");
  const [quickFilter, setQuickFilter] = useState(null);
  const [globalPreset, setGlobalPreset] = useState({});
  // Oct 2026 — server-side windowing: the Analytics Panel reports the date
  // range it needs ({from,to} or null for All Time); only that slice of the
  // ledgers is fetched. `undefined` = panel hasn't reported yet.
  const [dataWindow, setDataWindow] = useState(undefined);
  const windowRef = useRef(undefined);
  const [ledgersLoading, setLedgersLoading] = useState(false);
  const tabsRef = useRef(null);

  const loadLedgers = useCallback(async () => {
    const win = windowRef.current;
    const query = win ? { from: win.from, to: win.to } : {};
    setLedgersLoading(true);
    try {
      const [i, e] = await Promise.all([
        api.get("/finance/invoices", { params: query }),
        api.get("/finance/expenses", { params: query }),
      ]);
      setInvoices(i.data); setExpenses(e.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLedgersLoading(false); }
    if (can("finance_create") || can("finance_edit") || can("finance_delete")) {
      api.get("/finance/sources/balances").then(({ data }) => setLiquidity(data)).catch(() => setLiquidity(null));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    Promise.all([api.get("/finance/categories"), api.get("/daily-tasks/projects")])
      .then(([c, p]) => { setCategories(c.data?.categories || []); setProjectsList(p.data || []); })
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const handleWindowChange = useCallback((win) => {
    windowRef.current = win;
    setDataWindow(win);
  }, []);
  useEffect(() => { if (dataWindow !== undefined) loadLedgers(); }, [dataWindow, loadLedgers]);
  const load = loadLedgers;

  // deep-link support: the overdue-invoice reminder notification and other
  // cross-page links land here with ?ftab=<tab>&fq=overdue.
  useEffect(() => {
    const ftab = params.get("ftab");
    if (ftab) setActiveTab(ftab);
    if (params.get("fq") === "overdue") {
      setQuickFilter({ statuses: ["sent", "overdue"], label: "Overdue invoices" });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fired by the Analytics Panel whenever a bar/segment/StatCard is
  // clicked — switch to that ledger tab and scroll it into view.
  const handleNavigate = (tab, filter) => {
    if (tab !== activeTab && !confirmDiscardUnsaved()) return;
    setActiveTab(tab);
    setQuickFilter(filter || null);
    tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // role guard — UI safety net; backend is the real gate
  if (!can("finance")) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="finance-403">
        <PageHeader eyebrow="Restricted" title="Finance" subtitle="You don't have access to this module." />
        <div className="card-flat p-6 text-sm text-slate-600">You don&apos;t have access to this module. Ask a Founder/Admin to grant Finance access in Access Management.</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto fade-in" data-testid="finance-page">
      <PageHeader
        eyebrow="Money"
        title="Finance"
        subtitle="Spreadsheet-speed ledgers — edit any cell, then Bulk Save. Filter above, or click any bar to drill in."
        actions={
          <Button variant="outline" className="rounded-sm" onClick={load} data-testid="finance-refresh">
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
        }
      />

      <FinanceAnalyticsPanel
        invoices={invoices}
        expenses={expenses}
        projectsList={projectsList}
        onFilterChange={setGlobalPreset}
        onNavigate={handleNavigate}
        onWindowChange={handleWindowChange}
      />

      <LiquidityDashboard liquidity={liquidity} />

      <div className="flex items-center gap-2 mb-2 text-[11px] text-slate-500" data-testid="finance-data-window">
        <CalendarRange size={12} className="text-slate-400" />
        {dataWindow === undefined || ledgersLoading
          ? <span>Loading ledgers…</span>
          : dataWindow
            ? <span>Ledgers show <span className="font-medium text-slate-700">{formatDate(dataWindow.from)} → {formatDate(dataWindow.to)}</span> · switch the Time Navigator to <span className="font-medium text-slate-700">All Time</span> to load every row.</span>
            : <span>Ledgers show <span className="font-medium text-slate-700">all time</span> · {invoices.length} invoices, {expenses.length} expenses loaded.</span>}
      </div>
      {dataWindow === null && !ledgersLoading && (invoices.length >= ALL_TIME_ROW_LIMIT || expenses.length >= ALL_TIME_ROW_LIMIT) && (
        <div className="mb-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="finance-row-limit-notice">
          All Time shows only the latest {ALL_TIME_ROW_LIMIT.toLocaleString("en-IN")} {invoices.length >= ALL_TIME_ROW_LIMIT && expenses.length >= ALL_TIME_ROW_LIMIT ? "invoices and expenses" : invoices.length >= ALL_TIME_ROW_LIMIT ? "invoices" : "expenses"} — older entries are not included in these ledgers or totals. Pick a year or quarter in the Time Navigator to see them.
        </div>
      )}

      <Tabs value={activeTab} onValueChange={(v) => { if (v !== activeTab && !confirmDiscardUnsaved()) return; setActiveTab(v); setQuickFilter(null); }} className="w-full" ref={tabsRef}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="expenses-ledger" data-testid="tab-expenses-ledger" className="rounded-sm">Expenses</TabsTrigger>
          <TabsTrigger value="revenue-ledger" data-testid="tab-revenue-ledger" className="rounded-sm">Revenue</TabsTrigger>
          <TabsTrigger value="petty-cash" data-testid="tab-petty-cash" className="rounded-sm">Petty Cash</TabsTrigger>
          <TabsTrigger value="project-pnl" data-testid="tab-project-pnl" className="rounded-sm">Project P&amp;L</TabsTrigger>
          {isManager && <TabsTrigger value="management-dashboard" data-testid="tab-management-dashboard" className="rounded-sm">Management Dashboard</TabsTrigger>}
        </TabsList>

        <TabsContent value="expenses-ledger" className="mt-4">
          <ExpensesLedger rows={expenses} categories={categories} onChanged={load} presetFilters={globalPreset} quickFilter={quickFilter} onClearFilter={() => setQuickFilter(null)} />
        </TabsContent>

        <TabsContent value="revenue-ledger" className="mt-4">
          <RevenueLedger rows={invoices} categories={categories} onChanged={load} presetFilters={globalPreset} quickFilter={quickFilter} onClearFilter={() => setQuickFilter(null)} />
        </TabsContent>

        <TabsContent value="petty-cash" className="mt-4">
          <PettyCashLedger month="all" />
        </TabsContent>

        <TabsContent value="project-pnl" className="mt-4">
          <ProjectPnLTab />
        </TabsContent>

        {isManager && (
          <TabsContent value="management-dashboard" className="mt-4">
            <ManagementDashboard />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
